import React, { type JSX } from 'react';

import { Button, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridCsvGetRowsToExportParams,gridDataRowIdsSelector, useGridApiContext } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReactComponent as CustomExportIcon } from 'assets/icons/Icon-Download-Export.svg';
import { PaginationMode } from 'common/constants/paginationMode';
import { FetchExportDataResult } from 'common/types/TypeDefs';

import { setExportDialogOpen } from '../exportDialog/exportDialogSlice';
import { selectPageInfo, selectPaginationGridData, setPaginationGridData, setRows } from '../PalmDataGrid/palmDataGridSlice';

const ExportIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>((props, ref) => (
    <CustomExportIcon  ref={ref} {...props}/>
));
ExportIcon.displayName = 'ExportIcon';

type ExportButtonProps = {
    onExportHandler?: () => Promise<FetchExportDataResult>;
    title?: string;
};

export function ExportButton({
    onExportHandler,
    title
}: Readonly<ExportButtonProps>): JSX.Element {
    const theme = useTheme();
    const exportIconTooltipDescription = 'Export';
    const BUTTON_TEST_ID = 'export-data-grid-button-test-id';
    const apiRef = useGridApiContext();
    const dispatch = useAppDispatch();
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const pageInfo = useAppSelector(selectPageInfo);

    const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) => {
        return gridDataRowIdsSelector(apiRef);
    };

    const onExportButtonClick = async () => {
        const fetchExportDataResult = await onExportHandler?.();
        const isServerPaginationExport = !fetchExportDataResult?.isClientSidePagination;
        const shouldExport = fetchExportDataResult?.isSuccessful;

        if(shouldExport){

            if(isServerPaginationExport){
                dispatch(setPaginationGridData({ ...paginationGridData, paginationMode: PaginationMode.Client }));
                dispatch(setRows(fetchExportDataResult.data ?? []));
                apiRef.current.setRows(fetchExportDataResult.data ?? []);
            }

            const fileName = pageInfo?.name ? `${pageInfo.name} - ${title ?? 'Export'}` : title ?? 'Export';
            apiRef.current.exportDataAsCsv({
                getRowsToExport: isServerPaginationExport ? getFilteredRows : undefined,
                fileName
            });

            if(isServerPaginationExport){
                dispatch(setPaginationGridData({ ...paginationGridData, paginationMode: PaginationMode.Server }));
                triggerPaginatedRowsUpdate();
            }

            dispatch(setExportDialogOpen(false));
        }
    };

    const triggerPaginatedRowsUpdate = () => {
        apiRef.current.setRows(paginationGridData?.rows ?? []);
    };

    const exportIconWithTooltip = (
        <Tooltip title={exportIconTooltipDescription}>
            <ExportIcon style={{ color: theme.palette.primary.accent }} />
        </Tooltip>
    );

    return (
        <Button
            sx={{
                padding: '4px',
                minWidth: 'auto',
                marginRight: '1.5rem',
                border: null,
                backgroundColor: 'transparent'
            }}
            onClick={onExportButtonClick}
            data-testid={BUTTON_TEST_ID}
        >
            {exportIconWithTooltip}
        </Button>
    );
}
